<template>
  <div class="lin-tree-wrap" :class="{ compact, horizontal }" ref="wrap">
    <div class="lin-tree" :style="{ width: L.width + 'px', height: L.height + 'px' }" @mouseleave="hl = null">
      <svg class="lin-edges" :width="L.width" :height="L.height" :viewBox="`0 0 ${L.width} ${L.height}`" aria-hidden="true">
        <path v-for="(e, i) in L.edges" :key="i" :d="edgePath(e, S)" class="lin-edge" :class="[e.kind, { on: onPath(e) }]" />
      </svg>
      <div v-for="g in L.groups" :key="'g' + g.parent" class="lin-group" :style="{ left: g.x + 'px', top: g.y + 'px', width: g.w + 'px', height: g.h + 'px' }">
        <span class="lin-group-label">{{ groupLabel(g) }}</span>
      </div>
      <div v-for="cd in L.cards" :key="cd.n.id" class="lin-slot" :style="{ left: cd.x + 'px', top: cd.y + 'px' }">
        <lineage-card :n="cd.n" :zh="zh" :from="from" :root="!!cd.root && !compact" :hl="hl !== null && lineage.has(cd.n.id)" :current="cd.n.id === current" @enter="hl = $event" @leave="hl = null" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, nextTick, watch } from 'vue'
import { layoutForest, transposeLayout, edgePath, SIZES } from './tree-layout.mjs'
import LineageCard from './LineageCard.vue'

const props = defineProps({ trees: { type: Array, required: true }, zh: Boolean, from: String, compact: Boolean, current: String, horizontal: Boolean })   // horizontal：代往右排（作品頁 mode:'row'、圖譜頁切換）
const S = SIZES
const L = computed(() => props.horizontal ? transposeLayout(layoutForest(props.trees, S), S) : layoutForest(props.trees, S))

// hover 一張卡：亮起它的整條祖先線與直接子代
const parentOf = computed(() => {
  const m = {}
  const walk = (n, p) => { if (p) m[n.id] = p; for (const c of n.c || []) walk(c, n.id) }
  for (const t of props.trees) walk(t, null)
  return m
})
const childrenOf = computed(() => {
  const m = {}
  const walk = (n) => { m[n.id] = (n.c || []).map(c => c.id); for (const c of n.c || []) walk(c) }
  for (const t of props.trees) walk(t)
  return m
})
const hl = ref(null)
// 樹比容器寬時（手機、或 1,482px 那幾棵），一開始就把「這件」或根捲到視窗中間；
// 不然使用者看到的是左上角一片空，根在畫面外
const wrap = ref(null)
function centerOn () {
  const el = wrap.value
  if (!el || el.clientWidth >= L.value.width) return
  const target = L.value.cards.find(c => c.n.id === props.current) || L.value.cards.find(c => c.root) || L.value.cards[0]
  if (target) el.scrollLeft = Math.max(0, target.x + S.CW / 2 - el.clientWidth / 2)
}
onMounted(() => nextTick(centerOn))
watch(() => props.trees, () => nextTick(centerOn))
const lineage = computed(() => {
  const s = new Set()
  if (hl.value === null) return s
  let cur = hl.value
  while (cur) { s.add(cur); cur = parentOf.value[cur] }
  for (const c of childrenOf.value[hl.value] || []) s.add(c)
  return s
})
const onPath = e => hl.value !== null && e.to && lineage.value.has(e.to) && lineage.value.has(e.from)
const groupLabel = g => props.zh
  ? `${g.n} 件${g.kind === 'fork' ? 'fork 自此' : g.kind === 'sim' ? '推論延續' : '衍生'}`
  : `${g.n} ${g.kind === 'fork' ? 'forked from this' : g.kind === 'sim' ? 'inferred' : 'descendants'}`
</script>

<style>
.lin-tree-wrap{overflow-x:auto;overflow-y:hidden;padding:6px 2px 14px;-webkit-overflow-scrolling:touch}
.lin-tree{position:relative}
.lin-edges{position:absolute;left:0;top:0;pointer-events:none;overflow:visible}
.lin-edge{fill:none;stroke:var(--ink-4,#55555a);stroke-width:1.5}
.lin-edge.sim{stroke-dasharray:4 4}
.lin-edge.group{stroke-dasharray:none;stroke:var(--ink-4,#55555a)}
.lin-edge.on{stroke:var(--gold,#d9c267);stroke-width:2}
.lin-group{position:absolute;border:0;border-radius:var(--r-md);background:var(--tint-1);box-sizing:border-box}
.lin-group-label{position:absolute;left:10px;top:3px;font-size:.68rem;letter-spacing:.06em;text-transform:uppercase;color:var(--ink-3,#85858a)}
.lin-slot{position:absolute}
</style>
